/**
 * Equipery / Pumper compatibility layer.
 *
 * Pumper remains in control of the main product-page form. Theme-owned forms,
 * cart-drawer upsells and Shipping Protection bypass Pumper and use the native
 * browser fetch captured before app scripts can wrap Shopify cart requests.
 */
(() => {
  'use strict';

  const nativeFetch = window.fetch.bind(window);
  const PRODUCT_FORM_SELECTOR = 'form[action*="/cart/add"]';
  const UPSELL_SELECTOR = 'cart-drawer-upsell, product-info-upsell';
  const SHIPPING_PROTECTION_SELECTOR =
    'cart-drawer-upsell[data-handle="equipery-shipping-protection"]';

  const pendingForms = new WeakSet();
  const pendingShippingProtection = new WeakSet();

  function getRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/?$/, '/')}${path}`;
  }

  function getCartAddUrl() {
    return getRoute('cart/add.js');
  }

  function getCartUpdateUrl() {
    return getRoute('cart/update.js');
  }

  function getCartJsonUrl() {
    return getRoute('cart.js');
  }

  function getCart() {
    return (
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification')
    );
  }

  function getSections(cart) {
    if (!cart || typeof cart.getSectionsToRender !== 'function') return [];

    return cart
      .getSectionsToRender()
      .map((section) => section.id)
      .filter(Boolean);
  }

  function toPositiveInteger(value, fallback = 1) {
    const parsed = Number.parseInt(String(value ?? ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  async function requestJson(url, options = {}) {
    const response = await nativeFetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });

    let data;

    try {
      data = await response.json();
    } catch (_error) {
      data = {};
    }

    if (!response.ok || data.status) {
      throw new Error(
        data.description ||
          data.message ||
          `Shopify cart request failed (${response.status}).`
      );
    }

    return data;
  }

  async function hydrateMissingSections(cartData, sections) {
    if (cartData.sections || sections.length === 0) return cartData;

    const sectionsData = await requestJson(
      `${getRoute('cart')}?sections=${encodeURIComponent(
        sections.join(',')
      )}`
    );

    cartData.sections = sectionsData;
    return cartData;
  }

  function setLoading(trigger, loading) {
    if (!trigger) return;

    trigger.disabled = loading;
    trigger.classList?.toggle('loading', loading);
    trigger.setAttribute?.(
      'aria-disabled',
      loading ? 'true' : 'false'
    );
  }

  function showCartError(message) {
    const errorContainer = document.querySelector(
      '#CartDrawer-CartErrors, .cart-drawer .cart__warnings, cart-drawer .cart-errors'
    );

    if (errorContainer) {
      errorContainer.textContent =
        message || 'Something went wrong.';
    }
  }

  function announceCartUpdate(
    cartData,
    item,
    source = 'equipery-independent-product-form'
  ) {
    const detail = {
      source,
      productVariantId: String(item.id),
      cartData,
    };

    if (typeof window.publish === 'function') {
      window.publish('cart-update', detail);
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', { detail })
    );
  }

  async function renderCartUpdate(cartData, item, source) {
    const cart = getCart();
    const sections = getSections(cart);

    await hydrateMissingSections(cartData, sections);
    announceCartUpdate(cartData, item, source);

    if (cart && typeof cart.renderContents === 'function') {
      cart.renderContents(cartData, false);

cart.classList.remove('is-empty');
cart.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    } else {
      window.location.assign(
        window.routes?.cart_url || getRoute('cart')
      );
    }
  }

  function syncUpsellForm(upsell) {
    if (!(upsell instanceof Element)) return;

    const productId = upsell.dataset.productId;
    const variantId = upsell.dataset.id;

    if (!productId || !variantId) return;

    upsell
      .querySelectorAll(PRODUCT_FORM_SELECTOR)
      .forEach((form) => {
        form
          .querySelectorAll('input[name="product-id"]')
          .forEach((input) => {
            input.value = productId;
            input.setAttribute('value', productId);
          });

        const variantInput =
          form.querySelector('input[name="id"]');

        if (variantInput) {
          variantInput.value = variantId;
          variantInput.setAttribute('value', variantId);
        }
      });
  }

  function syncAllUpsellForms() {
    document
      .querySelectorAll(UPSELL_SELECTOR)
      .forEach(syncUpsellForm);
  }

  function resolveIndependentForm(trigger) {
    const explicitFormId =
      trigger?.dataset?.equiperyProductFormId;

    const form = explicitFormId
      ? document.getElementById(explicitFormId)
      : trigger?.form;

    if (
      !form ||
      !form.matches(PRODUCT_FORM_SELECTOR)
    ) {
      return null;
    }

    const productForm = form.closest('product-form');

    if (
      !productForm ||
      productForm.classList.contains('main-product-form')
    ) {
      return null;
    }

    return { form, productForm };
  }

  function resolveIndependentSubmit(form) {
    if (
      !(form instanceof HTMLFormElement) ||
      !form.matches(PRODUCT_FORM_SELECTOR)
    ) {
      return null;
    }

    const productForm = form.closest('product-form');

    if (
      !productForm ||
      productForm.classList.contains('main-product-form')
    ) {
      return null;
    }

    return { form, productForm };
  }

  function resolveShippingProtectionToggle(trigger) {
    if (
      !trigger?.matches?.(
        '.upsell-toggle-btn, .upsell__toggle-switch, .toggle-switch'
      )
    ) {
      return null;
    }

    const upsell = trigger.closest(
      SHIPPING_PROTECTION_SELECTOR
    );

    if (!upsell) return null;

    syncUpsellForm(upsell);

    return { upsell, trigger };
  }

  function getItem(form, trigger) {
    const formData = new FormData(form);
    const upsell = trigger?.closest?.(UPSELL_SELECTOR);

    const variantId =
      trigger?.dataset?.equiperyVariantId ||
      upsell?.dataset?.id ||
      formData.get('id');

    if (!variantId) {
      throw new Error(
        'Independent Add to Cart is missing a variant ID.'
      );
    }

    const item = {
      id: String(variantId),
      quantity: toPositiveInteger(
        formData.get('quantity'),
        1
      ),
    };

    const sellingPlan = formData.get('selling_plan');

    if (sellingPlan) {
      item.selling_plan = String(sellingPlan);
    }

    const properties = {};

    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.+)]$/);

      if (!match || value === '') continue;

      properties[match[1]] = value;
    }

    if (Object.keys(properties).length) {
      item.properties = properties;
    }

    return item;
  }

  async function addIndependentProduct(
    form,
    productForm,
    trigger
  ) {
    if (pendingForms.has(form)) return;

    pendingForms.add(form);
    setLoading(trigger, true);

    try {
      const cart = getCart();
      const sections = getSections(cart);
      const item = getItem(form, trigger);

      const cartData = await requestJson(
        getCartAddUrl(),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [item],
            sections,
            sections_url: window.location.pathname,
          }),
        }
      );

      await renderCartUpdate(
        cartData,
        item,
        'equipery-independent-product-form'
      );
    } catch (error) {
      console.error(
        '[Equipery] Independent Add to Cart failed:',
        error
      );

      const errorWrapper =
        productForm.querySelector?.(
          '.product-form__error-message-wrapper'
        );

      const errorMessage =
        errorWrapper?.querySelector?.(
          '.product-form__error-message'
        );

      if (errorWrapper) {
        errorWrapper.hidden = false;
      }

      if (errorMessage) {
        errorMessage.textContent =
          error.message || 'Something went wrong.';
      }

      showCartError(error.message);
    } finally {
      pendingForms.delete(form);
      setLoading(trigger, false);
    }
  }

  async function toggleShippingProtection(
    upsell,
    trigger
  ) {
    if (pendingShippingProtection.has(upsell)) return;

    const variantId = String(
      upsell.dataset.id || ''
    );

    if (!variantId) {
      console.error(
        '[Equipery] Shipping Protection is missing its variant ID.'
      );
      return;
    }

    const wasSelected =
      upsell.dataset.selected === 'true';

    const shouldSelect = !wasSelected;

    pendingShippingProtection.add(upsell);
    setLoading(trigger, true);

    try {
      const currentCart = await requestJson(
        getCartJsonUrl()
      );

      const matchingLines = (
        currentCart.items || []
      ).filter(
        (line) =>
          String(line.variant_id || line.id) ===
          variantId
      );

      let cartData = currentCart;
      let cartChanged = false;

      const cart = getCart();
      const sections = getSections(cart);

      if (
        shouldSelect &&
        matchingLines.length === 0
      ) {
        cartData = await requestJson(
          getCartAddUrl(),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity: 1,
                },
              ],
              sections,
              sections_url:
                window.location.pathname,
            }),
          }
        );

        cartChanged = true;
      } else if (
        !shouldSelect &&
        matchingLines.length > 0
      ) {
        const updates = {};

        matchingLines.forEach((line) => {
          updates[line.key] = 0;
        });

        cartData = await requestJson(
          getCartUpdateUrl(),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              updates,
              sections,
              sections_url:
                window.location.pathname,
            }),
          }
        );

        cartChanged = true;
      }

      upsell.dataset.selected = shouldSelect
        ? 'true'
        : 'false';

      trigger.setAttribute(
        'aria-pressed',
        shouldSelect ? 'true' : 'false'
      );

      if (cartChanged) {
        await renderCartUpdate(
          cartData,
          { id: variantId },
          'equipery-shipping-protection'
        );
      }
    } catch (error) {
      upsell.dataset.selected = wasSelected
        ? 'true'
        : 'false';

      trigger.setAttribute(
        'aria-pressed',
        wasSelected ? 'true' : 'false'
      );

      console.error(
        '[Equipery] Shipping Protection update failed:',
        error
      );

      showCartError(error.message);
    } finally {
      pendingShippingProtection.delete(upsell);
      setLoading(trigger, false);
    }
  }

  function stopAppHandlers(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  /*
   * Capture clicks before Pumper and other application
   * handlers receive them.
   */
  window.addEventListener(
    'click',
    (event) => {
      const trigger =
        event.target?.closest?.(
          'button, input[type="submit"]'
        );

      if (!trigger || trigger.disabled) return;

      const closestUpsell =
        trigger.closest?.(UPSELL_SELECTOR);

      if (closestUpsell) {
        syncUpsellForm(closestUpsell);
      }

      const shippingProtection =
        resolveShippingProtectionToggle(trigger);

      if (shippingProtection) {
        stopAppHandlers(event);

        toggleShippingProtection(
          shippingProtection.upsell,
          shippingProtection.trigger
        );

        return;
      }

      const resolved =
        resolveIndependentForm(trigger);

      if (!resolved) return;

      stopAppHandlers(event);

      addIndependentProduct(
        resolved.form,
        resolved.productForm,
        trigger
      );
    },
    true
  );

  /*
   * Covers requestSubmit() and programmatic
   * submit-button clicks.
   */
  window.addEventListener(
    'submit',
    (event) => {
      const resolved =
        resolveIndependentSubmit(event.target);

      if (!resolved) return;

      const upsell =
        resolved.form.closest(UPSELL_SELECTOR);

      if (upsell) {
        syncUpsellForm(upsell);
      }

      stopAppHandlers(event);

      const trigger =
        event.submitter ||
        resolved.productForm.querySelector(
          '[type="submit"]'
        );

      addIndependentProduct(
        resolved.form,
        resolved.productForm,
        trigger
      );
    },
    true
  );

  function startUpsellObserver() {
    syncAllUpsellForms();

    new MutationObserver(
      syncAllUpsellForms
    ).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startUpsellObserver,
      { once: true }
    );
  } else {
    startUpsellObserver();
  }
})();